/**
 * WhatsApp WABA API Client.
 * Uses Meta Business Graph API v18.0.
 *
 * ⚠️  CONVENTION — READ-ONLY ENDPOINTS MUST USE CACHED METHODS
 * ─────────────────────────────────────────────────────────────
 * All GET calls MUST use 'GET_CACHED_ONE_HOUR' (or another cached variant)
 * unless there is an explicit, documented reason for using raw 'GET'.
 *
 * Why: React re-render loops (e.g. unstable useCallback deps) can fire
 * the same fetch hundreds of times per second. Cached calls are served from
 * memory/IndexedDB and never hit the Cloud Function or Facebook Graph API,
 * so a loop with cached calls is harmless. Uncached 'GET' calls bypass the
 * cache entirely and hit the CF on every invocation — an infinite loop with
 * uncached calls will exhaust Facebook's rate limit within seconds.
 *
 * If you need fresh data, call the cached method and pass `uncached = true`
 * only from a user-initiated action (button click), never from useEffect.
 */

import type { APIResponse } from '@/types/common';
import { proxyRequest } from '@/lib/api/proxy';
import { isWaUserId } from '@/lib/utils/phone';
import type {
  WhatsAppSendResponse,
  WhatsAppWABAInfoResponse,
  WhatsAppBusinessProfileResponse,
  WhatsAppUpdateBusinessProfilePayload,
  WhatsAppTemplatesResponse,
  WhatsAppCatalogResponse,
  WhatsAppCallSettings,
  WhatsAppCallSettingsResponse,
  WhatsAppCallInitiateResponse,
  WhatsAppCallAcceptResponse,
  WhatsAppCallButtonPayload,
  WhatsAppCreateTemplatePayload,
  WhatsAppEditTemplatePayload,
  WhatsAppTemplateActionResponse,
} from './whatsapp-types';
import { WHATSAPP_GRAPH_API } from './whatsapp-types';

export class WhatsAppAPI {
  private extensionName: string;

  constructor(extensionName: string) {
    this.extensionName = extensionName;
  }

  private get tpaService(): string {
    return this.extensionName.includes('for')
      ? this.extensionName.split('for')[0]
      : this.extensionName;
  }

  private async apiCall<T = unknown>(
    url: string,
    method: string,
    data?: unknown,
    headers?: Record<string, string>
  ): Promise<APIResponse<T>> {
    return proxyRequest<T>({
      appCode: this.extensionName,
      url,
      method,
      data,
      headers: headers ?? { 'Content-Type': 'application/json' },
      TPAService: this.tpaService,
    });
  }

  // ── WABA Info ──

  async getWABAInfo(wabaId: string, uncached = false): Promise<APIResponse<WhatsAppWABAInfoResponse>> {
    return this.apiCall<WhatsAppWABAInfoResponse>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/?fields=ownership_type,phone_numbers{display_phone_number,verified_name,quality_rating},on_behalf_of_business_info`,
      uncached ? 'GET' : 'GET_CACHED_ONE_HOUR'
    );
  }

  /** Uncached WABA info call — used for auth checking to avoid stale 401 cache */
  async getWABAInfoUncached(): Promise<APIResponse<WhatsAppWABAInfoResponse>> {
    return this.apiCall<WhatsAppWABAInfoResponse>(
      `${WHATSAPP_GRAPH_API}/{{WABA_ID}}/?fields=ownership_type,phone_numbers{display_phone_number,verified_name},on_behalf_of_business_info`,
      'GET'
    );
  }

  // ── Subscribed Apps & Registration Status ──

  /**
   * Get apps subscribed to the WABA (checks webhook subscription).
   * Cached 1 day at the proxy layer — `getWABAStatuses` runs on every
   * page load (drives the "incoming sync not configured" amber alert)
   * and this response only changes when the user explicitly toggles
   * incoming sync. Cache is busted by `clearProxyCacheByUrl('subscribed_apps')`
   * in incoming-sync-settings / whatsapp-incoming when the user makes
   * a config change.
   */
  async getSubscribedApps(wabaId: string): Promise<APIResponse<{ data: Array<{ id: string; name: string; link: string; override_callback_uri?: string }> }>> {
    return this.apiCall<{ data: Array<{ id: string; name: string; link: string; override_callback_uri?: string }> }>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/subscribed_apps`,
      'GET_CACHED_ONE_DAY'
    );
  }

  /**
   * Get WABA info with phone status fields (status, name_status).
   * Cached 1 day — same rationale as `getSubscribedApps` above. Phone
   * registration status / name approval changes via explicit user
   * action; the cache is busted by
   * `clearProxyCacheByUrl('on_behalf_of_business_info')` after such
   * changes.
   */
  async getWABAInfoWithStatus(wabaId: string): Promise<APIResponse<WhatsAppWABAInfoResponse>> {
    return this.apiCall<WhatsAppWABAInfoResponse>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/?fields=ownership_type,phone_numbers{display_phone_number,verified_name,quality_rating,status,name_status},on_behalf_of_business_info`,
      'GET_CACHED_ONE_DAY'
    );
  }

  /** Subscribe an app to the WABA (with optional webhook override) */
  async subscribeApp(wabaId: string, data?: {
    override_callback_uri?: string;
    verify_token?: string;
  }): Promise<APIResponse<{ success: boolean }>> {
    return this.apiCall<{ success: boolean }>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/subscribed_apps`,
      'POST',
      data
    );
  }

  /** Unsubscribe an app from the WABA (temporarily stop receiving webhooks) */
  async unsubscribeApp(wabaId: string): Promise<APIResponse<{ success: boolean }>> {
    return this.apiCall<{ success: boolean }>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/subscribed_apps`,
      'DELETE'
    );
  }

  async clearCredentials(): Promise<APIResponse<{ status: string }>> {
    return this.apiCall<{ status: string }>('--clearcredentials', 'POST');
  }

  /** Register a phone number for WhatsApp messaging */
  async configureDataLocalization(phoneNumberId: string, region: string): Promise<APIResponse<{ success: boolean }>> {
    return this.apiCall<{ success: boolean }>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/settings`,
      'POST',
      {
        storage_configuration: {
          status: 'IN_COUNTRY_STORAGE_ENABLED',
          data_localization_region: region,
        },
      }
    );
  }

  async registerPhoneNumber(phoneNumberId: string, pin: string): Promise<APIResponse<{ success: boolean }>> {
    return this.apiCall<{ success: boolean }>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/register`,
      'POST',
      { messaging_product: 'whatsapp', pin }
    );
  }

  // ── Business Profile ──

  async getBusinessProfile(phoneNumberId: string): Promise<APIResponse<WhatsAppBusinessProfileResponse>> {
    return this.apiCall<WhatsAppBusinessProfileResponse>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/whatsapp_business_profile?fields=about,address,description,email,profile_picture_url,websites,vertical`,
      'GET_CACHED_ONE_HOUR'
    );
  }

  async updateBusinessProfile(
    phoneNumberId: string,
    payload: WhatsAppUpdateBusinessProfilePayload
  ): Promise<APIResponse<{ success: boolean }>> {
    return this.apiCall<{ success: boolean }>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/whatsapp_business_profile`,
      'POST',
      payload
    );
  }

  // ── Templates ──

  async getTemplates(wabaId: string, uncached = false): Promise<APIResponse<WhatsAppTemplatesResponse>> {
    return this.apiCall<WhatsAppTemplatesResponse>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/message_templates?status=APPROVED&limit=200`,
      uncached ? 'GET' : 'GET_CACHED_ONE_HOUR'
    );
  }

  /** Follow pagination cursor for templates */
  async getTemplatesByUrl(nextUrl: string, uncached = false): Promise<APIResponse<WhatsAppTemplatesResponse>> {
    return this.apiCall<WhatsAppTemplatesResponse>(nextUrl, uncached ? 'GET' : 'GET_CACHED_ONE_HOUR');
  }

  // ── Template Management ──

  /** Fetch ALL templates (all statuses) for dashboard management — not cached */
  async getAllTemplates(wabaId: string): Promise<APIResponse<WhatsAppTemplatesResponse>> {
    return this.apiCall<WhatsAppTemplatesResponse>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/message_templates?limit=200`,
      'GET'
    );
  }

  /** Create a new template */
  async createTemplate(
    wabaId: string,
    payload: WhatsAppCreateTemplatePayload
  ): Promise<APIResponse<WhatsAppTemplateActionResponse>> {
    return this.apiCall<WhatsAppTemplateActionResponse>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/message_templates`,
      'POST',
      payload
    );
  }

  /** Edit an existing template (only components can be changed) */
  async editTemplate(
    templateId: string,
    payload: WhatsAppEditTemplatePayload
  ): Promise<APIResponse<WhatsAppTemplateActionResponse>> {
    return this.apiCall<WhatsAppTemplateActionResponse>(
      `${WHATSAPP_GRAPH_API}/${templateId}`,
      'POST',
      payload
    );
  }

  /** Delete a template by name (deletes ALL language versions) */
  async deleteTemplate(
    wabaId: string,
    templateName: string
  ): Promise<APIResponse<WhatsAppTemplateActionResponse>> {
    return this.apiCall<WhatsAppTemplateActionResponse>(
      `${WHATSAPP_GRAPH_API}/${wabaId}/message_templates?name=${encodeURIComponent(templateName)}`,
      'DELETE'
    );
  }

  /** Migrate approved templates from one WABA to another (same Meta Business required) */
  async migrateMessageTemplates(
    destinationWabaId: string,
    sourceWabaId: string,
    pageNumber = 0,
  ): Promise<APIResponse<{ total: number; migrated_count?: number; data?: unknown[] }>> {
    return this.apiCall<{ total: number; migrated_count?: number; data?: unknown[] }>(
      `${WHATSAPP_GRAPH_API}/${destinationWabaId}/migrate_message_templates`,
      'POST',
      {
        source_waba_id: sourceWabaId,
        page_number: pageNumber,
      }
    );
  }

  // ── Messaging ──

  async sendMessage(
    senderPhoneNumberId: string,
    payload: Record<string, unknown>
  ): Promise<APIResponse<WhatsAppSendResponse>> {
    return this.apiCall<WhatsAppSendResponse>(
      `${WHATSAPP_GRAPH_API}/${senderPhoneNumberId}/messages`,
      'POST',
      payload
    );
  }

  /** Upload media to WhatsApp via Meta's media upload API. Returns media ID.
   *  `fileUrl` is a download URL — the CF downloads the file and uploads to Meta as multipart. */
  async uploadMedia(
    phoneNumberId: string,
    fileUrl: string,
    mimeType: string,
    fileName: string
  ): Promise<APIResponse<{ id: string }>> {
    return this.apiCall<{ id: string }>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/media`,
      'POST',
      {
        messaging_product: 'whatsapp',
        type: mimeType,
        attachments: [{ mediaUrl: fileUrl, fileName }],
      },
      { 'Content-Type': 'multipart/form-data' }
    );
  }

  // ── Catalogs & Products ──

  /** Fetch catalogs linked to a WABA ID (with products) */
  async getCatalogs(wabaId: string): Promise<APIResponse<WhatsAppCatalogResponse>> {
    return this.apiCall<WhatsAppCatalogResponse>(
      `${WHATSAPP_GRAPH_API}/${wabaId}?fields=product_catalogs{products.limit(100){image_url,id,name,price,visibility,short_description,availability,url,retailer_id},name,product_count}`,
      'GET_CACHED_ONE_HOUR'
    );
  }

  /** Fetch business-owned catalogs (by business ID) */
  async getBusinessOwnedCatalogs(
    businessId: string
  ): Promise<APIResponse<WhatsAppCatalogResponse>> {
    return this.apiCall<WhatsAppCatalogResponse>(
      `${WHATSAPP_GRAPH_API}/${businessId}?fields=owned_product_catalogs{products.limit(100){image_url,id,name,price,visibility,short_description,availability,url,retailer_id},name,product_count}`,
      'GET_CACHED_ONE_HOUR'
    );
  }

  // ── Media ──

  /** Get download URL for a media file by media ID */
  async getMediaUrl(mediaId: string): Promise<APIResponse<{ url: string; mime_type: string }>> {
    return this.apiCall<{ url: string; mime_type: string }>(
      `${WHATSAPP_GRAPH_API}/${mediaId}`,
      'GET'
    );
  }

  // ── Calling API ──

  /** Get current calling settings for a phone number */
  async getCallSettings(phoneNumberId: string): Promise<APIResponse<WhatsAppCallSettingsResponse>> {
    return this.apiCall<WhatsAppCallSettingsResponse>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/settings`,
      'GET'
    );
  }

  /** Configure or update calling settings for a phone number */
  async updateCallSettings(
    phoneNumberId: string,
    settings: { calling: Partial<WhatsAppCallSettings> }
  ): Promise<APIResponse<WhatsAppCallSettingsResponse>> {
    return this.apiCall<WhatsAppCallSettingsResponse>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/settings`,
      'POST',
      { messaging_product: 'whatsapp', ...settings }
    );
  }

  /** Initiate a business-initiated call */
  async initiateCall(
    phoneNumberId: string,
    to: string,
    sdpOffer: string
  ): Promise<APIResponse<WhatsAppCallInitiateResponse>> {
    return this.apiCall<WhatsAppCallInitiateResponse>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/calls`,
      'POST',
      {
        messaging_product: 'whatsapp',
        // Meta accepts a BSUID (username-only user) only in `recipient`
        ...(isWaUserId(to) ? { recipient: to } : { to }),
        action: 'connect',
        session: {
          sdp_type: 'offer',
          sdp: sdpOffer,
        },
      }
    );
  }

  /** Accept an incoming user-initiated call */
  async acceptCall(
    phoneNumberId: string,
    callId: string,
    sdpAnswer: string
  ): Promise<APIResponse<WhatsAppCallAcceptResponse>> {
    return this.apiCall<WhatsAppCallAcceptResponse>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/calls`,
      'POST',
      {
        messaging_product: 'whatsapp',
        call_id: callId,
        action: 'accept',
        session: {
          sdp_type: 'answer',
          sdp: sdpAnswer,
        },
      }
    );
  }

  /** Reject an incoming user-initiated call */
  async rejectCall(
    phoneNumberId: string,
    callId: string
  ): Promise<APIResponse<{ success: boolean }>> {
    return this.apiCall<{ success: boolean }>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/calls`,
      'POST',
      {
        messaging_product: 'whatsapp',
        call_id: callId,
        action: 'reject',
      }
    );
  }

  /** Terminate an active call */
  async terminateCall(
    phoneNumberId: string,
    callId: string
  ): Promise<APIResponse<{ success: boolean }>> {
    return this.apiCall<{ success: boolean }>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/calls`,
      'POST',
      {
        messaging_product: 'whatsapp',
        call_id: callId,
        action: 'terminate',
      }
    );
  }

  /** Send an interactive message with a call-to-action voice call button */
  async sendCallButtonMessage(
    phoneNumberId: string,
    payload: WhatsAppCallButtonPayload
  ): Promise<APIResponse<WhatsAppSendResponse>> {
    return this.apiCall<WhatsAppSendResponse>(
      `${WHATSAPP_GRAPH_API}/${phoneNumberId}/messages`,
      'POST',
      payload
    );
  }
}
